import {GameActions} from '../actions/game_actions';
import * as AlphaUtils from '../../utils/alpha';
import * as GameUtils from '../../utils/game';
import * as WordUtils from '../../utils/words';

const MAX_VOWELS = 5;

export const GameSelectors = {
  energy: (state) => state.game.energy,
  letters: (state) => state.game.boxes,
  drain: (state) => state.game.drain,
  word: (state) => state.game.word,
  surviveLevel: (state) => state.game.survive.level,
  bestWords: (state) => state.game.bestWords,
  health: (state) => state.game.health,
  status: (state) => state.game.status,
  score: (state) => state.game.score,
  isValid: (state) => state.game.isValid,
  isVowelMaxed: (state) => state.game.isVowelMaxed,
  vowels: (state) => state.game.vowels,
  text: (state) => state.game.text,
  level: (state) => state.game.level,
};

const initialState = {
  // control
  locked: false,
  status: GameUtils.GameStatus.INIT,
  game: GameUtils.GameType.SURVIVE,
  survive: {
    level: 0,
  },
  // game
  boxes: [
    {val: GameUtils.blank, index: 0},
    {val: GameUtils.blank, index: 1},
    {val: GameUtils.blank, index: 2},
    {val: GameUtils.blank, index: 3},
    {val: GameUtils.blank, index: 4},
    {val: GameUtils.blank, index: 5},
    {val: GameUtils.blank, index: 6},
    {val: GameUtils.blank, index: 7},
    {val: GameUtils.blank, index: 8},
    {val: GameUtils.blank, index: 9},
    {val: GameUtils.blank, index: 10},
    {val: GameUtils.blank, index: 11},
  ],
  notify: {
    drain: false,
  },
  /*
  effects: {
    poison: {
      inEffect: false,
      turnsLeft: 0,
      power: 0,
    },
  },
  */
  vowels: [],
  maxVowels: MAX_VOWELS,
  isVowelMaxed: false,
  word: [],
  text: '',
  score: 0,
  isValid: false,
  /*
  enemy: {
    health: 50,
    attacks: {
      drain: {
        power: 4,
      },
      poison: {
        cooldown: 4,
        duration: 3,
        power: 2,
      }
    },
  },
  */
  // Enemy
  health: 50,
  drain: 4,
  foe: 'A',
  // Player
  energy: 100,
  bestWords: [],
  wordLength: {},
};

export default function(state = initialState, action) {
  const {type, payload} = action;

  const newNotify = {
    drain: false,
  };

  switch (type) {
    case GameActions.ATTACK_DRAIN: {
      const {drainAmount} = payload;
      newNotify.drain = true;
      return {
        ...state,
        energy: state.energy - drainAmount,
        notify: newNotify,
      };
    }
    case GameActions.NEW_GAME: {
      return {
        ...initialState,
        notify: newNotify,
        status: GameUtils.GameStatus.PLAY,
        bestWords: state.bestWords,
        boxes: GameUtils.populateBox(),
      };
    }
    case GameActions.UPDATE: {
      const {updates} = payload;
      return {
        ...state,
        ...updates,
        notify: newNotify,
      };
    }
    case GameActions.CLEAR_WORD: {
      return {
        ...state,
        notify: newNotify,
        word: [],
        text: '',
        score: 0,
        isValid: false,
      };
    }
    case GameActions.CHANGE_ENERGY: {
      const {newEnergy} = payload;
      return {
        ...state,
        notify: newNotify,
        energy: newEnergy,
      };
    }
    case GameActions.WIPE_WORD: {
      // Replace missing letters
      const newBoxes = [...state.boxes];
      for (let index = 0; index < 12; index++) {
        if (newBoxes[index].val.val === '') {
          newBoxes[index] = {index: index, val: AlphaUtils.getLetter()};
        }
      }
      return {
        ...state,
        notify: newNotify,
        boxes: newBoxes,
        word: [],
        text: '',
        score: 0,
        isValid: false,
      };
    }
    case GameActions.TURN: {
      const {health} = payload;
      return {
        ...state,
        notify: newNotify,
        health: health,
      };
    }
    case GameActions.NEW_LETTERS: {
      if (state.energy <= GameUtils.REFRESH_COST) {
        return state;
      } else {
        return {
          ...state,
          notify: newNotify,
          energy: state.energy - GameUtils.REFRESH_COST,
          boxes: GameUtils.populateBox(),
        };
      }
    }
    case GameActions.BLANK_LETTER: {
      const {index} = payload;
      const newBoxes = [...state.boxes];
      newBoxes[index] = {index: index, val: AlphaUtils.blank};
      return {
        ...state,
        notify: newNotify,
        boxes: newBoxes,
      };
    }
    case GameActions.BLANK_VOWEL: {
      const {index} = payload;
      const newVowels = [...state.vowels];
      newVowels[index] = {index: index, val: AlphaUtils.blank};
      return {
        ...state,
        notify: newNotify,
        vowels: newVowels,
      };
    }
    case GameActions.APPEND_TO_WORD: {
      const {letterInfo} = payload;
      const newWord = [...state.word];
      newWord.push(letterInfo);
      const newText = WordUtils.convertToText(newWord);
      const newIsValid = WordUtils.isValid(newText);
      const newScore = WordUtils.score(newIsValid, newWord);
      return {
        ...state,
        notify: newNotify,
        word: newWord,
        text: newText,
        isValid: newIsValid,
        score: newScore,
      };
    }
    case GameActions.RESTORE_VOWEL: {
      const {letterInfo} = payload;
      const newVowels = [...state.vowels];
      newVowels[letterInfo.index] = letterInfo;
      return {
        ...state,
        notify: newNotify,
        vowels: newVowels,
      };
    }
    case GameActions.RESTORE_LETTER: {
      const {letterInfo} = payload;
      const newBoxes = [...state.boxes];
      newBoxes[letterInfo.index] = letterInfo;
      return {
        ...state,
        notify: newNotify,
        boxes: newBoxes,
      };
    }
    case GameActions.UPDATE_WORD: {
      const {word} = payload;
      const newText = WordUtils.convertToText(word);
      const newIsValid = WordUtils.isValid(newText);
      const newScore = WordUtils.score(newIsValid, word);
      return {
        ...state,
        notify: newNotify,
        isValid: newIsValid,
        word: word,
        text: newText,
        score: newScore,
      };
    }
    case GameActions.SET_BEST_WORDS: {
      const {newBestWords} = payload;
      return {
        ...state,
        notify: newNotify,
        bestWords: newBestWords,
      };
    }
    case GameActions.ADD_VOWEL: {
      const {vowel} = payload;
      const newVowels = [...state.vowels];
      const newIndex = newVowels.length;
      newVowels.push({
        index: newIndex,
        val: vowel,
      });
      const newIsVowelMaxed = newVowels.length >= state.maxVowels;
      return {
        ...state,
        notify: newNotify,
        vowels: newVowels,
        isVowelMaxed: newIsVowelMaxed,
      };
    }
    case GameActions.WIN: {
      return {
        ...state,
        notify: newNotify,
        status: GameUtils.GameStatus.VICTORY,
      };
    }
    case GameActions.COMPRESS_VOWELS: {
      const newVowels = [];
      let index = 0;
      state.vowels.forEach((vowel) => {
        if (vowel.val.vowel) {
          newVowels.push({index: index, val: vowel.val});
          index += 1;
        }
      });
      const newIsVowelMaxed = newVowels.length >= state.maxVowels;
      return {
        ...state,
        notify: newNotify,
        vowels: newVowels,
        isVowelMaxed: newIsVowelMaxed,
      };
    }
    case GameActions.PLAYER_DEFEATED: {
      return {
        ...state,
        notify: newNotify,
        energy: 0,
        status: GameUtils.GameStatus.DEFEAT,
      };
    }
    default: {
      return state;
    }
  }
}

